import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TenantCreateInput, TenantDTO } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { Dialog } from '../../components/a11y/Dialog';
import { AttemptStatusBadge } from '../../components/payments/PaymentActivity';
import { formatCents } from '../../lib/money';
import { formatRelative } from '../../lib/date';
import { portalStatus } from '../../lib/portal-status';
import { Link } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider';

/**
 * Tenants — the canonical admin CRUD screen (the pattern the other admin pages mirror):
 * a react-query list + a "New" button, per-row Edit/Delete, and a create/edit form in an
 * accessible {@link Dialog}. All writes go through the shared `api` client (CSRF handled there)
 * and invalidate the list query on success. `firstName`/`lastName` are required; the rest optional.
 */

const TENANTS_KEY = ['admin', 'tenants'] as const;

/** Empty → undefined, so optional string fields are omitted rather than sent as "". */
function opt(v: string): string | undefined {
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

function TenantForm({ tenant, onClose }: { tenant: TenantDTO | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const editing = tenant != null;
  const [firstName, setFirstName] = useState(tenant?.firstName ?? '');
  const [lastName, setLastName] = useState(tenant?.lastName ?? '');
  const [email, setEmail] = useState(tenant?.email ?? '');
  const [phone, setPhone] = useState(tenant?.phone ?? '');
  const [notes, setNotes] = useState(tenant?.notes ?? '');

  const save = useMutation({
    mutationFn: () => {
      const body: TenantCreateInput = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: opt(email) ?? null,
        phone: opt(phone) ?? null,
        notes: opt(notes) ?? null,
      };
      return editing ? api.tenants.update(tenant.id, body) : api.tenants.create(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENANTS_KEY });
      onClose();
    },
  });

  return (
    <Dialog title={editing ? 'Edit tenant' : 'New tenant'} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        <div>
          <label htmlFor="t-first">First name</label>
          <input id="t-first" value={firstName} required maxLength={200} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="t-last">Last name</label>
          <input id="t-last" value={lastName} required maxLength={200} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="t-email">Email (optional)</label>
          <input id="t-email" type="email" value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label htmlFor="t-phone">Phone (optional)</label>
          <input id="t-phone" value={phone} maxLength={40} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div>
          <label htmlFor="t-notes">Notes (landlord-internal, optional)</label>
          <textarea id="t-notes" value={notes} rows={3} maxLength={2000} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create tenant'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function AdminTenants() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: TENANTS_KEY, queryFn: () => api.tenants.list() });
  const { principal } = useAuth();
  const isOwner = principal?.role === 'owner';
  // `undefined` = closed; `null` = create; a tenant = edit.
  const [editing, setEditing] = useState<TenantDTO | null | undefined>(undefined);

  const remove = useMutation({
    mutationFn: (t: TenantDTO) => api.tenants.remove(t.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TENANTS_KEY }),
  });

  // Portal invite — sends the tenant a passwordless magic-link sign-in email (find-or-creates
  // their login on the server). Needs an email on the tenant; the button is disabled without one.
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: (t: TenantDTO) => api.tenants.invite(t.id),
    onSuccess: (_res, t) => setInviteMsg(`Sign-in link sent to ${t.email}.`),
  });

  if (query.isLoading) return <Loading label="Loading tenants…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const tenants = query.data.tenants;

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Tenants</h1>
        <button type="button" onClick={() => setEditing(null)}>
          New tenant
        </button>
      </div>
      <p className="subtitle">People who rent your units. Add a tenant here, then attach them to a lease.</p>

      {remove.isError && <StatusMessage tone="err">{errorMessage(remove.error)}</StatusMessage>}
      {invite.isError && <StatusMessage tone="err">{errorMessage(invite.error)}</StatusMessage>}
      {inviteMsg && <StatusMessage tone="ok">{inviteMsg}</StatusMessage>}

      {tenants.length === 0 ? (
        <p className="muted">No tenants yet. Choose “New tenant” to add one.</p>
      ) : (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table>
            <caption className="visually-hidden">Tenants</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Phone</th>
                <th scope="col">Portal</th>
                <th scope="col">Last online payment</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id}>
                  <td>
                    {t.firstName} {t.lastName}
                  </td>
                  <td>{t.email || '—'}</td>
                  <td>{t.phone || '—'}</td>
                  <td>
                    {(() => {
                      const s = portalStatus(t);
                      return <span className={s.cls}>{s.label}</span>;
                    })()}
                  </td>
                  <td>
                    {t.lastPaymentActivity ? (
                      <span>
                        {formatCents(t.lastPaymentActivity.amountCents)} <AttemptStatusBadge status={t.lastPaymentActivity.status} />{' '}
                        <span className="muted">{formatRelative(t.lastPaymentActivity.at)}</span>
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <div className="row">
                      <button type="button" className="secondary" onClick={() => setEditing(t)}>
                        Edit<span className="visually-hidden"> {t.firstName} {t.lastName}</span>
                      </button>
                      {isOwner && (
                        <Link className="button-link" to={`/admin/activity/tenant/${encodeURIComponent(t.id)}`}>
                          Activity<span className="visually-hidden"> for {t.firstName} {t.lastName}</span>
                        </Link>
                      )}
                      <button
                        type="button"
                        className="secondary"
                        disabled={invite.isPending || !t.email}
                        title={t.email ? 'Email a portal sign-in link' : 'Add an email first to invite'}
                        onClick={() => {
                          setInviteMsg(null);
                          invite.mutate(t);
                        }}
                      >
                        Invite<span className="visually-hidden"> {t.firstName} {t.lastName} to the portal</span>
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(`Delete tenant ${t.firstName} ${t.lastName}?`)) remove.mutate(t);
                        }}
                      >
                        Delete<span className="visually-hidden"> {t.firstName} {t.lastName}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined && <TenantForm tenant={editing} onClose={() => setEditing(undefined)} />}
    </>
  );
}
